
import { useAuthStore } from '../stores/auth.store';
import { buildApiError } from './apiError';
import { tokenScaduto } from './sessione';
import { broadcast, withRefreshLock, SESSION_MSG } from './sessionChannel';

const API_BASE = import.meta.env.VITE_API_BASE || '';
// No VITE_API_KEY. The gateway key is a server-side secret: nginx adds the
// X-API-Key header to every proxied request (see frontend/nginx.conf.template).
// Inlining it here shipped the shared secret to every browser that loaded
// the app, which made the API-key guard on the backends worthless.

// Single source of truth: every token read goes through useAuthStore.getState().
// Zustand's getState is sync and cheap; no separate cache to drift. Token
// writes happen exclusively from auth.store actions (login / setTokens /
// _ingestRotatedTokens / logout). This is the architectural inversion that
// eliminates the "tokens in localStorage but not in memory yet" race that
// produced spurious 401s on the very first API call after login.

function _store() { return useAuthStore.getState(); }

function getAccessToken() { return _store().accessToken || null; }
function getRefreshTokenInternal() { return _store().refreshToken || null; }

// Decode the access token's `exp` (ms epoch), or null if absent/unparseable.
// Used to schedule the proactive refresh just before expiry AND to decide
// whether an arriving 401 plausibly reflects token expiry at all.
export function getAccessTokenExpiry() {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json);
    return payload?.exp ? payload.exp * 1000 : null;
  } catch (_) {
    return null;
  }
}

// "Auth boundary" = login OR successful refresh. Within ACCESS_FRESHNESS_MS
// of the most recent boundary, ANY 401 is overwhelmingly likely to be a
// data-side issue (the token literally can't have expired in that window).
// We use this gate to suppress the spurious "Sessione persa" modal.
const ACCESS_FRESHNESS_MS = 30 * 1000;
function _accessTokenLooksFresh() {
  const s = _store();
  const boundary = Math.max(s.loginAt || 0, s.lastRefreshAt || 0);
  if (boundary && Date.now() - boundary < ACCESS_FRESHNESS_MS) return true;
  const exp = getAccessTokenExpiry();
  if (exp && exp > Date.now() + 5000) return true;
  return false;
}

// Methods that imply DELIBERATE user action (vs. background polling).
// A successful response to one of these counts as session activity, which
// keeps the inactivity timer from draining while the user is clicking
// around even if they happen to be still on the mouse.
const ACTIVITY_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function _dispatchActivity(source) {
  try {
    window.dispatchEvent(new CustomEvent('wfm-activity', { detail: { source, at: Date.now() } }));
  } catch (_) { /* CustomEvent unsupported — non-fatal */ }
  // The session is one; its inactivity countdown should be too. Without this
  // the window holding the softphone can time out while the user is working
  // in another one.
  broadcast(SESSION_MSG.activity, { source });
}

export async function apiFetch(path, options = {}) {
  const token = getAccessToken();
  const method = (options.method || 'GET').toUpperCase();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    // Layer 1: an `upstream: true` body marker means the 401 came from a
    // downstream service (IvozProvider etc.), NOT WFM auth. Surface as a
    // normal error — never touch the session.
    const bodyForCheck = await res.clone().json().catch(() => ({}));
    if (bodyForCheck?.error?.upstream === true || bodyForCheck?.error?.kind === 'terminal') {
      const err = new Error(bodyForCheck?.error?.message || 'Errore upstream');
      err.code = bodyForCheck?.error?.code || 'UPSTREAM_AUTH';
      err.status = res.status;
      err.upstream = true;
      throw err;
    }

    // Layer 2: token looks demonstrably fresh (just-logged-in OR JWT.exp
    // still in the future). A 401 here is a data/permission issue or a
    // server bug — not a session expiry. Don't broadcast, don't refresh,
    // just surface as a normal error so the caller's UI handles it.
    if (_accessTokenLooksFresh()) {
      const err = new Error(bodyForCheck?.error?.message || `Errore ${res.status}`);
      err.code = bodyForCheck?.error?.code || 'UNAUTHORIZED_DATA';
      err.status = res.status;
      throw err;
    }

    // Layer 3: try a real refresh. Single-flighted so a 401 storm collapses
    // to ONE /auth/refresh call.
    const refreshed = await tryRefresh();
    if (refreshed) return apiFetch(path, options);

    // Layer 4: refresh genuinely failed. Suppress the modal on /login and
    // /onboarding (the user is on an auth page anyway), AND debounce the
    // broadcast so a 10-call burst can't fan out into 10 modal openings.
    const currentPath = window.location.pathname;
    if (currentPath !== '/login' && currentPath !== '/onboarding') {
      _maybeBroadcastSessionExpired();
    }
    throw new Error('Sessione scaduta');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Carries both the fetch-client fields and the axios shape — see apiError.js.
    throw buildApiError(res.status, body);
  }

  // Successful mutation counts as user activity — the user just *did*
  // something. Pure GETs don't bump the activity stream, because background
  // polling (notifications, presence) would otherwise keep an AFK session
  // alive forever.
  if (res.ok && ACTIVITY_METHODS.has(method)) {
    _dispatchActivity('api:' + method.toLowerCase());
  }

  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return res.json();
  }
  if (options.expectJson === false) return res;
  const previewText = await res.text().catch(() => '');
  const previewLooksHtml = /^\s*<(!doctype|html|head|body)/i.test(previewText);
  const err = new Error(previewLooksHtml
    ? `Endpoint ${path} non instradato (risposta HTML invece di JSON). Verifica la configurazione del proxy / nginx.`
    : `Risposta non JSON da ${path} (content-type: ${contentType || 'sconosciuto'}).`);
  err.code = 'INVALID_RESPONSE';
  err.status = res.status;
  throw err;
}

// Debounced session-expired broadcaster. SessionGuard reacts to the event by
// opening its modal; a single user action can fan out into 5–10 parallel API
// calls, and without this guard every one of them would broadcast on a 401
// storm, painting the modal with a torrent of events.
let _lastExpiredBroadcastAt = 0;
function _maybeBroadcastSessionExpired() {
  const now = Date.now();
  if (now - _lastExpiredBroadcastAt < 1000) return;
  _lastExpiredBroadcastAt = now;
  window.dispatchEvent(new CustomEvent('wfm-session-expired'));
}

// Single-flight refresh. When the access token expires, a page typically
// fires several API calls at once — each gets a 401 and would call refresh.
// Without single-flighting, the first /auth/refresh rotates the token and the
// rest send the now-stale token → 401 → spurious logout. Collapsing
// concurrent 401s onto ONE refresh promise eliminates the stampede.
let refreshInFlight = null;

function tryRefresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

// Public wrapper so the session keep-alive (SessionGuard) can proactively
// refresh the access token before it expires — keeping an idle session alive
// without the user ever hitting a 401. Single-flighted with reactive refresh.
export function refreshSession() {
  return tryRefresh();
}

// Reasons doRefresh can fail — exposed via the last-failure helper below so
// SessionGuard can show a precise message instead of a silent logout.
// terminal = the refresh token is genuinely dead, retrying never helps.
// transient = network blip / server hiccup / malformed response, retry-worthy.
const REFRESH_FAILURE = { terminal: 'terminal', transient: 'transient', none: 'none' };
let _lastRefreshFailure = REFRESH_FAILURE.none;
// auth-service distinguishes SESSION_NOT_FOUND (the token was retired — the
// signature of two windows rotating the same one), SESSION_EXPIRED (the
// session really did reach its end), TOKEN_REVOKED and INVALID_TOKEN. That
// code was being thrown away, leaving every terminal failure looking alike
// and un-diagnosable after the fact. Keep it.
let _lastRefreshCode = null;
export function getLastRefreshFailure() { return _lastRefreshFailure; }
export function getLastRefreshCode() { return _lastRefreshCode; }

// Six attempts with exponential backoff — sums to ~16 s before transient
// gives up. The old 1.75 s ceiling was too short for slow mobile networks /
// VPN handshakes / Wi-Fi roaming, leading to a "transient" tag the user
// would have called "broken session". The active retry loop in SessionGuard
// continues from where this leaves off, but starting with more attempts
// here lets the proactive refresh recover most network blips silently.
const REFRESH_RETRY_DELAYS_MS = [250, 500, 1000, 2000, 4000, 8000];

// auth-service issues a 7-day refresh token to a browser tab and a 30-day one
// to an installed PWA — but only if the client says which it is. `isPwa` was
// never sent, so the longer PWA lifetime was dead configuration and installed
// users were being expired on the desktop schedule.
function _isStandalone() {
  try {
    return window.matchMedia?.('(display-mode: standalone)')?.matches
      || window.navigator?.standalone === true;
  } catch (_) {
    return false;
  }
}

async function doRefresh() {
  // ── Impersonazione ──────────────────────────────────────────────────────
  // Il token di impersonazione dura 5 minuti e **non ha un refresh token**:
  // quello che la scheda tiene in mano è dell'amministratore. Rinnovarlo qui
  // faceva due danni in silenzio: rimetteva in circolo l'identità
  // dell'amministratore sotto la fascia rossa «stai visualizzando come…», e
  // ritirava il suo refresh token — che «Termina impersonazione» rimetteva in
  // mano un istante dopo, ormai morto. Da lì ogni chiamata rispondeva «Token
  // non valido o scaduto», compreso il rientro.
  //
  // Un'impersonazione scaduta non si rinnova: finisce. Si torna al proprio
  // posto, e lo si dice.
  const stato = _store();
  if (stato.impersonating) {
    const tornatoAPosto = stato.stopImpersonation();
    try {
      window.dispatchEvent(new CustomEvent('wfm-impersonation-expired'));
    } catch (_) { /* CustomEvent non supportato — non fatale */ }
    if (tornatoAPosto && !tokenScaduto(getAccessToken())) {
      // La sessione dell'amministratore è viva: la richiesta che ha portato
      // qui viene ripetuta con la sua identità, che è quella giusta adesso.
      _lastRefreshFailure = REFRESH_FAILURE.none;
      _lastRefreshCode = null;
      return true;
    }
    // Era scaduta anche la sessione di chi impersonava: si prosegue con il
    // rinnovo normale, che ora usa il suo refresh token invece di rinnovare
    // sotto un'identità presa in prestito.
  }

  const tokenAtEntry = getRefreshTokenInternal();
  if (!tokenAtEntry) {
    _lastRefreshFailure = REFRESH_FAILURE.terminal;
    return false;
  }
  // Only one window may rotate at a time. The refresh token is single-use:
  // two windows rotating the same one means the loser presents a token the
  // server has already retired, which reads as "session closed elsewhere".
  return withRefreshLock(() => _doRefreshExclusive(tokenAtEntry));
}

async function _doRefreshExclusive(tokenAtEntry) {
  // We may have queued on the lock while another window rotated. Its
  // broadcast already put a fresh pair in this window's store, so the work
  // is done — rotating again would retire the pair that window is using.
  const refreshToken = getRefreshTokenInternal();
  if (refreshToken && refreshToken !== tokenAtEntry) {
    _lastRefreshFailure = REFRESH_FAILURE.none;
    return true;
  }
  if (!refreshToken) {
    _lastRefreshFailure = REFRESH_FAILURE.terminal;
    return false;
  }

  for (let attempt = 0; attempt <= REFRESH_RETRY_DELAYS_MS.length; attempt += 1) {
    let outcome;
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken, isPwa: _isStandalone() }),
      });
      if (res.status >= 400 && res.status < 500) {
        let code = null;
        try { code = (await res.json())?.error?.code || null; } catch (_) { /* no body */ }
        _lastRefreshCode = code;
        _lastRefreshFailure = REFRESH_FAILURE.terminal;
        return false;
      }
      if (!res.ok) {
        outcome = 'transient';
      } else {
        let body;
        try { body = await res.json(); } catch { body = null; }
        const newAccess = body?.data?.accessToken;
        if (typeof newAccess !== 'string' || newAccess.length < 10) {
          outcome = 'transient';
        } else {
          const newRefresh = body.data.refreshToken || refreshToken;
          // Land tokens in the single source of truth. The store action also
          // bumps lastRefreshAt so the freshness gate is immediately tight.
          useAuthStore.getState()._ingestRotatedTokens(newAccess, newRefresh);
          // Hand the rotated pair to the other windows before they try to use
          // the one we just retired.
          broadcast(SESSION_MSG.tokens, { accessToken: newAccess, refreshToken: newRefresh });
          _lastRefreshFailure = REFRESH_FAILURE.none;
          _lastRefreshCode = null;
          return true;
        }
      }
    } catch (_) {
      outcome = 'transient';
    }
    if (outcome === 'transient' && attempt < REFRESH_RETRY_DELAYS_MS.length) {
      await new Promise((r) => setTimeout(r, REFRESH_RETRY_DELAYS_MS[attempt]));
      continue;
    }
    _lastRefreshFailure = REFRESH_FAILURE.transient;
    return false;
  }
  _lastRefreshFailure = REFRESH_FAILURE.transient;
  return false;
}

export function buildQuery(params) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    if (Array.isArray(v)) {
      v.forEach((item) => qs.append(k, item));
    } else {
      qs.set(k, String(v));
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

export async function downloadBlob(path, filename) {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error(`Download fallito: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Default export: axios-compatible object for stores
const api = {
  get:    (path, opts) => apiFetch(path, { ...opts, method: 'GET' }),
  post:   (path, body, opts) => apiFetch(path, { ...opts, method: 'POST', body: JSON.stringify(body) }),
  put:    (path, body, opts) => apiFetch(path, { ...opts, method: 'PUT', body: JSON.stringify(body) }),
  patch:  (path, body, opts) => apiFetch(path, { ...opts, method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path, opts) => apiFetch(path, { ...opts, method: 'DELETE' }),
};
export default api;
